import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { requestNotifications } from "../../services/readerActivity";
import ActivityNotification from "./ActivityNotification";
import { combineReleaseCopies, isNotificationRead, notificationId, notificationTime } from "./circleActivityUtils";
import useCircleReads from "./useCircleReads";
import "./CircleActivity.css";
import ReaderDiscovery from './ReaderDiscovery';

export default function RHome() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [owner, setOwner] = useState(null);
  const requestVersion = useRef(0), requestAbort = useRef(null);
  const { readIds, readsOwner, markRead, readError, readsLoading, refreshReads } = useCircleReads();
  const feed = useMemo(() => combineReleaseCopies(notifications)
    .sort((a, b) => notificationTime(b) - notificationTime(a)), [notifications]);
  const unread = feed.filter((item) => !isNotificationRead(item, readIds)).length;
  const loadFeed = useCallback(async () => {
    const version = ++requestVersion.current;
    requestAbort.current?.abort();
    const controller = new AbortController(); requestAbort.current = controller;
    setLoading(true); setError(''); setNotifications([]); setOwner(null);
    try {
      const data = await requestNotifications({ signal: controller.signal });
      if (version !== requestVersion.current) return;
      setNotifications(data.notifications); setOwner(data.readerSub);
    } catch { if (version === requestVersion.current) setError('We couldn’t load your notifications. Please try again.'); }
    finally { if (version === requestVersion.current) setLoading(false); }
  }, []);
  const cancelFeed = useCallback(() => { ++requestVersion.current; requestAbort.current?.abort(); }, []);
  useEffect(() => {
    loadFeed();
    const refresh = () => { if (!document.hidden) loadFeed(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const interval = setInterval(() => { if (!document.hidden) { loadFeed(); refreshReads(); } }, 60000);
    return () => { cancelFeed(); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); clearInterval(interval); };
  }, [loadFeed, refreshReads, cancelFeed]);
  return <div className="max-w-4xl mx-auto">
    <p className="mb-5 text-sm text-bookish-purple"><Link to="?tab=quests" className="underline underline-offset-4">Your quest, if you choose to accept… Explore Circle Quests →</Link></p>
    <ReaderDiscovery />
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div><h2 className="text-3xl font-serif text-bookish-purple mb-2">Your latest updates</h2><p className="text-bookish-purpleAccent">Recent updates from the authors and genres you follow.</p></div>
      <Link to="?tab=mycircles" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-bookish-purple underline underline-offset-4">Browse My Circles</Link>
    </div>
    {(loading || readsLoading) && <p role="status">Gathering your latest updates...</p>}
    {error && <p role="alert" className="mb-4 text-red-700">{error} <button type="button" className="underline" onClick={loadFeed}>Try again</button></p>}
    {readError && <p role="alert" className="mb-4 text-red-700">{readError} <button type="button" className="underline" onClick={refreshReads}>Retry read status</button></p>}
    {!loading && !readsLoading && !error && !readError && owner !== readsOwner && <p role="alert">Your account changed. <button className="underline min-h-[44px]" onClick={() => { loadFeed(); refreshReads(); }}>Reload updates</button></p>}
    {!loading && !readsLoading && !error && !readError && owner === readsOwner && <section className="circle-activity" aria-label="All notifications">
      <div className="circle-activity-summary"><span><Bell size={18} aria-hidden="true" />{unread ? `${unread} unread update${unread === 1 ? "" : "s"}` : "You're all caught up"}</span><span>Most recent first · Up to 10 per followed author or genre</span></div>
      <div className="rounded-2xl border border-bookish-purple/15 bg-[#fffdf9] shadow-sm">
        {feed.length === 0 ? <p className="circle-empty">No notifications yet. Your latest author and genre updates will appear here.</p> :
          <div className="circle-update-list">{feed.map((raw) => <ActivityNotification key={notificationId(raw)} raw={raw} readIds={readIds} onRead={items => markRead(items.flatMap(item => item.notificationCopies || [item]))} />)}</div>}
      </div>
    </section>}
  </div>;
}
